import React, { createContext, useContext, useEffect, useState } from 'react';
import { getProfileImageUrl } from '../utils/profileImage';
import { supabase } from '../lib/supabaseClient';
import { decamelizeKeys } from '../utils/decamelize';

const AuthContext = createContext();

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return ctx;
};

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const sanitizeNamePart = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const splitFullName = (fullName) => {
  if (!fullName) {
    return { first: '', middle: '', last: '' };
  }

  const parts = String(fullName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { first: '', middle: '', last: '' };
  }

  if (parts.length === 1) {
    return { first: parts[0], middle: '', last: '' };
  }

  if (parts.length === 2) {
    return { first: parts[0], middle: '', last: parts[1] };
  }

  const first = parts.shift();
  const last = parts.pop();

  return {
    first: first ?? '',
    middle: parts.join(' '),
    last: last ?? ''
  };
};

const computeFullName = (firstName, middleName, lastName) =>
  [firstName, middleName, lastName]
    .map(sanitizeNamePart)
    .filter(Boolean)
    .join(' ');

const toSnakeKey = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const mergeStructuredNameFields = (baseFields, details, fullName) => {
  const structured = isPlainObject(baseFields) ? { ...baseFields } : {};

  const setOrDelete = (key, value) => {
    const snakeKey = toSnakeKey(key);
    if (value) {
      structured[key] = value;
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    } else {
      delete structured[key];
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    }
  };

  setOrDelete('firstName', details.firstName);
  setOrDelete('middleName', details.middleName);
  setOrDelete('lastName', details.lastName);

  if (fullName) {
    setOrDelete('fullName', fullName);
    setOrDelete('name', fullName);
  } else {
    setOrDelete('fullName', null);
    setOrDelete('name', null);
  }

  return structured;
};

const extractNameDetails = (source, fallbackFullName = '') => {
  const metadata = isPlainObject(source) ? source : {};
  const structuredSourceRaw = metadata.structuredFields ?? metadata.structured_fields;
  const structuredSource = isPlainObject(structuredSourceRaw)
    ? { ...structuredSourceRaw }
    : {};

  let firstName = sanitizeNamePart(
    metadata.firstName ??
    metadata.first_name ??
    structuredSource.firstName ??
    structuredSource.first_name
  );
  let middleName = sanitizeNamePart(
    metadata.middleName ??
    metadata.middle_name ??
    structuredSource.middleName ??
    structuredSource.middle_name
  );
  let lastName = sanitizeNamePart(
    metadata.lastName ??
    metadata.last_name ??
    structuredSource.lastName ??
    structuredSource.last_name
  );
  let fallback = sanitizeNamePart(
    metadata.fullName ??
    metadata.full_name ??
    structuredSource.fullName ??
    structuredSource.full_name ??
    metadata.name ??
    structuredSource.name ??
    fallbackFullName
  );

  if ((!firstName || !lastName) && fallback) {
    const parsed = splitFullName(fallback);
    if (!firstName) firstName = parsed.first;
    if (!middleName) middleName = parsed.middle;
    if (!lastName) lastName = parsed.last;
  }

  const fullName = computeFullName(firstName, middleName, lastName) || fallback;
  const structuredFields = mergeStructuredNameFields(
    structuredSource,
    { firstName, middleName, lastName },
    fullName
  );

  return { firstName, middleName, lastName, fullName, structuredFields };
};

const formatStructuredFields = (structuredFields) =>
  structuredFields && Object.keys(structuredFields).length > 0 ? structuredFields : null;

const buildUsersPfInsert = (authUser, metadataOverride) => {
  const metadata = metadataOverride ?? authUser?.user_metadata ?? {};
  const fallbackName = authUser?.email ? authUser.email.split('@')[0] : '';
  const { firstName, middleName, lastName, fullName, structuredFields } =
    extractNameDetails(metadata, fallbackName);

  return decamelizeKeys({
    authId: authUser.id,
    email: authUser.email,
    role: metadata.role || 'client',
    teamId: metadata.teamId || null,
    agentCode: metadata.agentCode || null,
    avatar: metadata.avatar_url || metadata.avatar || null,
    phone: metadata.phone || null,
    name: fullName || fallbackName || null,
    firstName: firstName || null,
    middleName: middleName || null,
    lastName: lastName || null,
    structuredFields: formatStructuredFields(structuredFields),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
};

const buildUsersPfNameUpdate = (authUser, metadataOverride) => {
  const metadata = metadataOverride ?? authUser?.user_metadata ?? {};
  const fallbackName = authUser?.email ? authUser.email.split('@')[0] : '';
  const { firstName, middleName, lastName, fullName, structuredFields } =
    extractNameDetails(metadata, fallbackName);

  return decamelizeKeys({
    authId: authUser.id,
    name: fullName || fallbackName || null,
    firstName: firstName || null,
    middleName: middleName || null,
    lastName: lastName || null,
    structuredFields: formatStructuredFields(structuredFields),
    updatedAt: new Date().toISOString(),
  });
};

const transformUser = (authUser, profile) => {
  if (!authUser) return null;

  // Use the profile ID if it exists, otherwise use auth user ID
  const userId = profile?.id || authUser.id;

  const metadata = authUser.user_metadata ?? {};
  const fallbackName =
    profile?.name ||
    metadata.fullName ||
    metadata.name ||
    (authUser.email ? authUser.email.split('@')[0] : '');

  const structuredSource =
    profile?.structured_fields ??
    profile?.structuredFields ??
    metadata.structuredFields ??
    metadata.structured_fields;

  const nameSource = {
    ...metadata,
    ...profile,
  };

  if (structuredSource) {
    nameSource.structuredFields = structuredSource;
    nameSource.structured_fields = structuredSource;
  }

  const { firstName, middleName, lastName, fullName, structuredFields } =
    extractNameDetails(nameSource, fallbackName);
  const resolvedFullName = fullName || fallbackName;

  return {
    id: userId, // This should be the ID from users_pf if it exists
    supabaseId: authUser.id, // Always keep the original auth ID for reference
    authId: authUser.id, // Explicit auth ID for clarity
    name: resolvedFullName,
    fullName: resolvedFullName,
    firstName,
    middleName,
    lastName,
    structuredFields,
    email: authUser.email,
    role: profile?.role || metadata.role || 'client',
    teamId: profile?.team_id || metadata.teamId,
    agentCode: profile?.agent_code || metadata.agentCode,

    avatar: profile?.avatar || metadata.avatar_url,
    profileImageUrl: profile?.profile_image_url,
    phone: profile?.phone || metadata.phone,
    imageUrl: getProfileImageUrl({
      profileImageUrl: profile?.profile_image_url,
      avatar: profile?.avatar || metadata.avatar_url
    }),
  };
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);

      if (event === 'TOKEN_REFRESH_FAILED') {
        await supabase.auth.signOut();
      }

      if (event === 'TOKEN_REFRESH_FAILED' || event === 'SIGNED_OUT') {
        setSession(null);
        setProfile(null);
        window.location.href = '/login';
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Load the user's profile to get the latest role
  useEffect(() => {
    const fetchProfile = async () => {
      if (!session?.user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        // First try to find by auth ID
        let { data, error } = await supabase
          .from('users_pf')
          .select('*')
          .eq('auth_id', session.user.id)
          .maybeSingle();

        // If not found by ID, try by email (for legacy users)
        if (!data && session.user.email) {
          console.log('User not found by ID, trying email lookup...');
          const emailResult = await supabase
            .from('users_pf')
            .select('*')
            .eq('email', session.user.email)
            .maybeSingle();
          
          data = emailResult.data;
          error = emailResult.error;
        }

        // If still no profile exists, create one
        if (!data && !error) {
          console.log('Creating new user profile...');
          const insertPayload = buildUsersPfInsert(session.user);
          const { data: newProfile, error: createError } = await supabase
            .from('users_pf')
            .insert(insertPayload)
            .select()
            .single();

          if (createError) {
            console.error('Error creating user profile:', createError);
            // If insert fails due to duplicate email, try to update the existing record
            if (createError.code === '23505') { // Unique constraint violation
              console.log('User exists with different ID, updating...');
              const updatePayload = buildUsersPfNameUpdate(session.user);
              const { data: updatedProfile, error: updateError } = await supabase
                .from('users_pf')
                .update(updatePayload)
                .eq('email', session.user.email)
                .select()
                .single();
              
              if (!updateError) {
                data = updatedProfile;
              } else {
                console.error('Error updating user profile:', updateError);
              }
            }
          } else {
            data = newProfile;
          }
        }

        if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
          console.error('Error fetching user profile:', error);
        }

        setProfile(data);
      } catch (err) {
        console.error('Unexpected error in fetchProfile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [session]);

  const signUp = async ({ email, password, data }) => {
    const { data: result, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/login`, data },
    });
    
    if (error) throw error;
    
    // After successful signup, create the user profile
    if (result?.user) {
      try {
        const insertPayload = buildUsersPfInsert(result.user, data);
        const { error: profileError } = await supabase
          .from('users_pf')
          .insert(insertPayload);

        if (profileError) {
          console.error('Error creating user profile during signup:', profileError);
        }
      } catch (err) {
        console.error('Error in signup profile creation:', err);
      }
    }
    
    return result;
  };

  const signIn = async ({ email, password }) => {
    const { data: result, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return result;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const updateProfile = async (updates) => {
    if (!session?.user) throw new Error('No authenticated user');
    
    // Update using the profile ID if it exists, otherwise use auth ID
    const userId = profile?.id || session.user.id;
    
    const { data: updatedProfile, error } = await supabase
      .from('users_pf')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();
      
    if (error) throw error;
    
    setProfile(updatedProfile);
    return updatedProfile;
  };

  const user = transformUser(session?.user, profile);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isAdvisor = user?.role === 'advisor';
  const isClient = user?.role === 'client';

  const hasRole = (role) => user?.role === role;
  const hasAnyRole = (roles) => roles.includes(user?.role);

  const value = {
    user,
    loading,
    isSignedIn: !!session,
    supabase,
    signUp,
    signIn,
    signOut,
    updateProfile,
    isAdmin,
    isManager,
    isAdvisor,
    isClient,
    hasRole,
    hasAnyRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
